import { AUSTRALIAN_STATES, CHANGE_TYPES } from '../../constants';
import { formatPercentage } from '../utils';


export function getProductHealthComplianceFieldName (tenant, forcedTenantRegion) {

	const region = forcedTenantRegion || tenant.region;

	if (region === AUSTRALIAN_STATES.VIC) {
		return 'HCGCategory';
	}

	if (region === AUSTRALIAN_STATES.NSW) {
		return 'HFDCategory';
	}

	// default to HCG
	return 'HCGCategory';
}

export function getProductHealthComplianceLocalValue (product, tenant, forcedTenantRegion) {
	if (!product) {
		return;
	}

	const region = forcedTenantRegion || tenant.region;

	if (region === AUSTRALIAN_STATES.VIC) {
		return product.HCGCategory;
	}

	if (region === AUSTRALIAN_STATES.NSW) {
		return product.HFDCategory;
	}

	// default to HCG
	return product.HCGCategory;
}

export function getProductHealthComplianceLocalLabel (tenant, short, forcedTenantRegion) {
	const region = forcedTenantRegion || tenant.region;

	if (region === AUSTRALIAN_STATES.VIC) {
		return getHCGLabel(short);
	}

	if (region === AUSTRALIAN_STATES.NSW) {
		return getHFDLabel(short);
	}

	// default to HCG
	return getHCGLabel(short);
}

export function getProductHealthComplianceLocalHint (tenant, forcedTenantRegion) {
	const region = forcedTenantRegion || tenant.region;

	if (region === AUSTRALIAN_STATES.VIC) {
		return getHCGHint();
	}

	if (region === AUSTRALIAN_STATES.NSW) {
		return getHFDHint();
	}

	// default to HCG
	return getHCGHint();
}

export function getHCGLabel (short) {
	return short ? 'HCG' : 'HCG category';
}

export function getHFDLabel (short) {
	return short ? 'HFD' : 'HFD category';
}

export function getHCGHint () {
	return 'Healthy choices guidelines categorisation';
}

export function getHFDHint () {
	return 'Healthy food and drink categorisation';
}

export function getPlanogramHealthCompliance (planogramItems, tenant, forcedTenantRegion) {
	const region = forcedTenantRegion || tenant.region;

	if (region === AUSTRALIAN_STATES.VIC) {
		return getHCGCompliance(planogramItems);
	}

	if (region === AUSTRALIAN_STATES.NSW) {
		return getHFDCompliance(planogramItems);
	}

	// default to HCG
	return getHCGCompliance(planogramItems);
}

// Pick lists swap in only products queued for the next service; next-empty queues wait until the selection sells out.
export function getPlanogramItemsAfterNextService (planogramItems) {
	return planogramItems.map(item => (
		item.queuedProduct && item.queuedProductType === CHANGE_TYPES.NEXT_SERVICE
			? { ...item, product: item.queuedProduct }
			: item
	));
}

export function hasProductsQueuedForNextService (planogramItems) {
	return planogramItems.some(item => item.enabled && item.queuedProduct && item.queuedProductType === CHANGE_TYPES.NEXT_SERVICE);
}

export function getHCGCompliance (planogramItems) {
	// This function returns a planograms HCG percentages,
	// and whether it is compliant with the HCG.

	if (!planogramItems) {
		return {};
	}

	const enabledPlanogramItems = planogramItems.filter(item => item.enabled === true);

	// Check for obscure yet possible scenario where
	// there are no planogram items (e.g. all are dexDisabled);
	// Prevents dividing by zero below.
	if (enabledPlanogramItems.length === 0) {
		return {};
	}

	let greenItems = 0;
	let amberItems = 0;
	let redItems = 0;
	let otherItems = 0;
	const totalItems = enabledPlanogramItems.length;

	enabledPlanogramItems.forEach(item => {
		if (item.product && item.product.HCGCategory) {
			if (item.product.HCGCategory === 'Green') {
				greenItems++;
			}
			else if (item.product.HCGCategory === 'Amber') {
				amberItems++;
			}
			else if (item.product.HCGCategory === 'Red') {
				redItems++;
			}
			else {
				otherItems++;
			}
		}
	});

	const numberOfSelections = enabledPlanogramItems.length;

	// Round items to 2 decimals so that HCG calculate is accurate
	let greenPercentage = parseFloat((greenItems / numberOfSelections).toFixed(2), 10);
	let amberPercentage = parseFloat((amberItems / numberOfSelections).toFixed(2), 10);
	let redPercentage = parseFloat((redItems / numberOfSelections).toFixed(2), 10);
	let otherPercentage = parseFloat((otherItems / numberOfSelections).toFixed(2), 10);

	let compliant = false;
	if (greenPercentage >= 0.5 && redPercentage <= 0.2) {
		compliant = true;
	}

	greenPercentage = formatPercentage(greenPercentage);
	amberPercentage = formatPercentage(amberPercentage);
	redPercentage = formatPercentage(redPercentage);
	otherPercentage = formatPercentage(otherPercentage);

	return {
		greenItems,
		amberItems,
		redItems,
		otherItems,
		totalItems,
		greenPercentage,
		amberPercentage,
		redPercentage,
		otherPercentage,
		compliant,
	};
}

export function getHFDCompliance (planogramItems) {
	// This function returns a planograms HCG percentages,
	// and whether it is compliant with the HCG.

	if (!planogramItems) {
		return {};
	}

	const enabledPlanogramItems = planogramItems.filter(item => item.enabled === true);

	// Check for obscure yet possible scenario where
	// there are no planogram items (e.g. all are dexDisabled);
	// Prevents dividing by zero below.
	if (enabledPlanogramItems.length === 0) {
		return {};
	}

	let everydayItems = 0;
	let occasionalItems = 0;
	let otherItems = 0;
	const totalItems = enabledPlanogramItems.length;

	enabledPlanogramItems.forEach(item => {
		if (item.product && item.product.HFDCategory) {
			if (item.product.HFDCategory === 'Everyday') {
				everydayItems++;
			}
			else if (item.product.HFDCategory === 'Occasional') {
				occasionalItems++;
			}
			else {
				otherItems++;
			}
		}
	});

	const numberOfSelections = enabledPlanogramItems.length;

	// Round items to 2 decimals so that HCG calculate is accurate
	let everydayPercentage = parseFloat((everydayItems / numberOfSelections).toFixed(2), 10);
	let occasionalPercentage = parseFloat((occasionalItems / numberOfSelections).toFixed(2), 10);
	let otherPercentage = parseFloat((otherItems / numberOfSelections).toFixed(2), 10);

	let compliant = false;
	if (everydayPercentage >= 0.75) {
		compliant = true;
	}

	everydayPercentage = formatPercentage(everydayPercentage);
	occasionalPercentage = formatPercentage(occasionalPercentage);
	otherPercentage = formatPercentage(otherPercentage);

	return {
		everydayItems,
		occasionalItems,
		otherItems,
		totalItems,
		everydayPercentage,
		occasionalPercentage,
		otherPercentage,
		compliant,
	};
}

// this is to resolve whether should display HCG or HFD related features like bulk field modification
export function getProductHealthComplianceVisibility (isInterstate, tenant, forcedTenantRegion) {
	if (isInterstate && !forcedTenantRegion) {
		return {
			shouldShowHCG: true,
			shouldShowHFD: true,
			shouldShowRed: true,
			shouldShowAmber: true,
			shouldShowGreen: true,
			shouldShowEveryday: true,
			shouldShowOccasional: true,
		};
	}

	const region = forcedTenantRegion || tenant.region;

	return {
		shouldShowHCG: region === AUSTRALIAN_STATES.VIC || !region,
		shouldShowHFD: region === AUSTRALIAN_STATES.NSW,
		shouldShowRed: region === AUSTRALIAN_STATES.VIC || !region,
		shouldShowAmber: region === AUSTRALIAN_STATES.VIC || !region,
		shouldShowGreen: region === AUSTRALIAN_STATES.VIC || !region,
		shouldShowEveryday: region === AUSTRALIAN_STATES.NSW,
		shouldShowOccasional: region === AUSTRALIAN_STATES.NSW,
	};
}
